/**
 * Converter for wrapped MCP server format
 *
 * Handles inputs like:
 * {
 *   "mcpServers": {
 *     "context7": {
 *       "url": "https://mcp.context7.com/mcp",
 *       "headers": { "CONTEXT7_API_KEY": "YOUR_API_KEY" }
 *     }
 *   }
 * }
 */

import type { ConverterEntry, McpServerConfig, ParsedMcpServer } from '../types';
import { isValidServerConfig, normalizeConfig } from './utils';

/**
 * Detects if input is in wrapped mcpServers format
 */
const detect = (input: unknown): boolean => {
  if (typeof input !== 'object' || input === null) {
    return false;
  }

  const obj = input as Record<string, unknown>;

  // Must have mcpServers key
  if (!('mcpServers' in obj)) {
    return false;
  }

  const { mcpServers } = obj;
  if (typeof mcpServers !== 'object' || mcpServers === null) {
    return false;
  }

  // mcpServers must contain at least one valid server config
  const servers = mcpServers as Record<string, unknown>;
  return Object.values(servers).some((config) => isValidServerConfig(config));
};

/**
 * Converts wrapped format to ParsedMcpServer array
 */
const convert = (input: unknown): ParsedMcpServer[] => {
  const obj = input as { mcpServers: Record<string, McpServerConfig> };
  return Object.entries(obj.mcpServers)
    .filter(([, config]) => isValidServerConfig(config))
    .map(([name, config]) => ({
      name,
      config: normalizeConfig(config),
    }));
};

export const wrappedFormatConverter: ConverterEntry = {
  format: 'wrapped',
  detect,
  convert,
  priority: 100, // Highest priority - most specific format
};
